import { MUIButton, MUIDialog, MUITextField } from "@abb/arcadia-ui-react"
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { useAccessRequests } from "../../../../hooks/useAccessRequests"
import { digitalManagerApi } from "../../../../services/api"
import { DiMaProgress } from "../../../components/DiMaProgress"
import { AppContext } from "../../../context/AppContext"
import { UserContext } from "../../../context/UserContext"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToFeature,
} from "../../../models/Authorization"
import { UserAccessRequest } from "../../../models/User"
import {
  dateStringComparator,
  getNiceFormattedDate,
} from "../../../utils/getFormattedDate"
import { EntityRow, EntityTab } from "../../partials/EntityTab"

interface BoxProps {
  readonly params: EntityRow
  readonly setDialogParams: (value: SetStateAction<EntityRow | undefined>) => void
  readonly setElaborateAccessRequest: (value: SetStateAction<boolean>) => void
  readonly userHasBackofficeWriteAccess: boolean
}

function Dialogs({
  elaborateAccessRequest,
  setElaborateAccessRequest,
  reloadAccessRequests,
  dialogParams,
}: {
  readonly elaborateAccessRequest: boolean
  readonly setElaborateAccessRequest: Dispatch<SetStateAction<boolean>>
  readonly reloadAccessRequests: () => void
  readonly dialogParams?: EntityRow
}) {
  const navigate = useNavigate()
  const [rejectAccessRequest, setRejectAccessRequest] = useState(false)
  const [additionalInformation, setAdditionalInformation] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleRejectSubmit = useCallback(() => {
    setIsSubmitting(true)
    digitalManagerApi
      .put(
        `/backOfficeAccess/requests/${dialogParams?.id}/reject/?additionalInformation=${additionalInformation}`
      )
      .then(() => {
        setElaborateAccessRequest(false)
        setRejectAccessRequest(false)
        toast.success("Access request rejected")
      })
      .catch(() => {
        toast.error("Cannot reject access request")
      })
      .finally(() => {
        reloadAccessRequests()
        setIsSubmitting(false)
      })
  }, [
    dialogParams?.id,
    additionalInformation,
    reloadAccessRequests,
    setElaborateAccessRequest,
  ])

  const navigateToPage = useCallback(() => {
    navigate(`/users/${dialogParams?.userId}#backoffice-access`)
    toast.success(
      "Welcome to the permissions management page. Please click on the toggle, fill out the form and click save to complete the operation."
    )
  }, [dialogParams?.userId, navigate])

  const handleCloseElaborateDialog = useCallback(
    () => setElaborateAccessRequest(false),
    [setElaborateAccessRequest]
  )

  const handleOpenRejectDialog = useCallback(() => {
    setRejectAccessRequest(true)
    setAdditionalInformation("")
  }, [setRejectAccessRequest, setAdditionalInformation])

  const handleCloseRejectDialog = useCallback(
    () => setRejectAccessRequest(false),
    [setRejectAccessRequest]
  )

  const handleAdditionalInformationChange = useCallback(
    (value: string) => {
      setAdditionalInformation(value)
    },
    [setAdditionalInformation]
  )

  return (
    <>
      <MUIDialog
        id="elaborate-access-request-dialog"
        variant="information"
        open={elaborateAccessRequest}
        onClose={handleCloseElaborateDialog}
        heading="Please read the request reason carefully before accepting"
        closeIconConfig={{ isDisabled: false, tooltipText: "Close" }}
        subtitle={`Reason: ${dialogParams?.reason ?? ""}`}
        primaryButtonText="Accept"
        onClickPrimaryButton={navigateToPage}
        primaryButtonConfig={{ type: "primary", size: 32, id: "accept-access-btn" }}
        tertiaryButtonConfig={{
          text: "Reject",
          onClick: handleOpenRejectDialog,
          type: "secondary",
          size: 32,
          id: "reject-access-btn",
        }}
      />

      <MUIDialog
        id="reject-access-request-dialog"
        variant="information"
        open={rejectAccessRequest}
        onClose={handleCloseRejectDialog}
        heading="Confirmation of rejection"
        closeIconConfig={{ isDisabled: false, tooltipText: "Close" }}
        content={
          <MUITextField
            placeholder="Please indicate the reason for the refusal of the access request. (Optional)"
            multiline
            rows={4}
            id="additionalInformation"
            name="additionalInformation"
            type="text"
            value={additionalInformation}
            onChange={handleAdditionalInformationChange}
            size={40}
          />
        }
        primaryButtonText="Reject"
        onClickPrimaryButton={handleRejectSubmit}
        isPrimaryButtonDisabled={isSubmitting}
        primaryButtonConfig={{
          isLoading: isSubmitting,
          type: "primary",
          size: 32,
          id: "reject-confirm-btn",
        }}
      />
    </>
  )
}

function CreateBox({
  params,
  setDialogParams,
  setElaborateAccessRequest,
  userHasBackofficeWriteAccess,
}: BoxProps) {
  const onClick = useCallback(() => {
    setDialogParams(params)
    setElaborateAccessRequest(true)
  }, [params, setDialogParams, setElaborateAccessRequest])

  return (
    <div>
      <MUIButton
        iconName="icon_abb_dias icon-abb_single-man-actions-check"
        onClickButton={onClick}
        disabled={!userHasBackofficeWriteAccess}
        label="View request"
        size={32}
      />
    </div>
  )
}

export function AccessRequests() {
  const { badges, setBadges } = useContext(AppContext)
  const { user } = useContext(UserContext)
  const [elaborateAccessRequest, setElaborateAccessRequest] = useState(false)
  const [dialogParams, setDialogParams] = useState<EntityRow>()

  const {
    accessRequestList,
    isLoadingAccessRequest,
    reloadAccessRequests,
    accessRequestsBadge,
  } = useAccessRequests()

  useEffect(() => {
    if (
      accessRequestsBadge !== undefined &&
      accessRequestsBadge !== badges.accessRequests
    ) {
      setBadges({ ...badges, accessRequests: accessRequestsBadge })
    }
  }, [badges, setBadges, accessRequestsBadge])

  if (!user) {
    return <DiMaProgress />
  }

  const userHasBackofficeWriteAccess = hasUserAccessToFeature(
    user.authorizations,
    DimaFeatureShortNameEnum.BA,
    AccessLevel.Write
  )

  const cols = [
    {
      field: "username",
      headerName: "Email address",
      renderCell: (row: EntityRow) => <div>{row.username}</div>,
      flex: 1,
    },
    {
      field: "creationDate",
      headerName: "Creation Date",
      renderCell: (row: EntityRow) => (
        <div>{getNiceFormattedDate(String(row.creationDate))}</div>
      ),
      sortComparator: dateStringComparator,
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      renderCell: (row: EntityRow) => (
        <CreateBox
          params={row}
          setDialogParams={setDialogParams}
          setElaborateAccessRequest={setElaborateAccessRequest}
          userHasBackofficeWriteAccess={userHasBackofficeWriteAccess}
        />
      ),
      flex: 1,
    },
  ]

  const rows: EntityRow[] = accessRequestList.map((req: UserAccessRequest) => ({
    id: req.id.toString(),
    userId: req.userId,
    username: req.username,
    creationDate: req.creationDate,
    completionDate: req.completionDate,
    rejectionDate: req.rejectionDate,
    reason: req.reason,
  }))

  return (
    <>
      <Dialogs
        elaborateAccessRequest={elaborateAccessRequest}
        setElaborateAccessRequest={setElaborateAccessRequest}
        reloadAccessRequests={reloadAccessRequests}
        dialogParams={dialogParams}
      />
      <EntityTab rows={rows} cols={cols} isLoading={isLoadingAccessRequest} />
    </>
  )
}
